import { useState, type CSSProperties } from "react";
import { ChevronUp, Clapperboard, FolderOpen, Video } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@web/lib/utils.ts";
import {
  SHOT_ASPECT_PIXELS,
  SHOT_TOTAL_MAX_MS,
  SHOT_TOTAL_MIN_MS,
  scaleShotTimingToTotal,
  shotTotalDurationMs,
  type ShotAspect,
} from "@web/features/globe/shotPreset.ts";
import {
  isMp4Mime,
  pickRecorderMimeType,
} from "@web/features/globe/recordFlight.ts";
import {
  pickOutputDirectory,
  supportsDirectoryPicker,
} from "@web/features/globe/shotSave.ts";
import { useGlobeShotStore } from "@web/stores/globeShot.ts";
import "@web/features/globe/shot.css";

function pct(value: number, min: number, max: number): string {
  if (max <= min) return "0%";
  return `${((value - min) / (max - min)) * 100}%`;
}

function ShotSlider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}) {
  return (
    <label className="block space-y-1">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="text-muted">{label}</span>
        <span className="tabular-nums text-faint">{format(value)}</span>
      </div>
      <input
        type="range"
        className="coykoi-shot-range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={
          {
            "--coykoi-range-progress": pct(value, min, max),
          } as CSSProperties
        }
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

/**
 * Collapsible Shot panel — docks under LayerStrip in the left chrome stack.
 */
export function ShotPanel({
  hidden,
  disabled,
  onPreview,
  onRecord,
}: {
  hidden?: boolean;
  disabled?: boolean;
  onPreview: () => void;
  onRecord: () => void;
}) {
  const collapsed = useGlobeShotStore((s) => s.panelCollapsed);
  const setCollapsed = useGlobeShotStore((s) => s.setPanelCollapsed);
  const presets = useGlobeShotStore((s) => s.presets);
  const activePresetId = useGlobeShotStore((s) => s.activePresetId);
  const setActivePresetId = useGlobeShotStore((s) => s.setActivePresetId);
  const values = useGlobeShotStore((s) => s.activeValues());
  const updateActiveValues = useGlobeShotStore((s) => s.updateActiveValues);
  const setEndAltitudeForAspect = useGlobeShotStore((s) => s.setEndAltitudeForAspect);
  const aspect = useGlobeShotStore((s) => s.aspect);
  const setAspect = useGlobeShotStore((s) => s.setAspect);
  const savePresetAs = useGlobeShotStore((s) => s.savePresetAs);
  const renameActivePreset = useGlobeShotStore((s) => s.renameActivePreset);
  const outputFolderName = useGlobeShotStore((s) => s.outputFolderName);
  const setOutputFolderName = useGlobeShotStore((s) => s.setOutputFolderName);
  const codecNoticeDismissed = useGlobeShotStore((s) => s.codecNoticeDismissed);
  const dismissCodecNotice = useGlobeShotStore((s) => s.dismissCodecNotice);
  const [saveName, setSaveName] = useState("");

  if (hidden) return null;

  if (collapsed) {
    return (
      <div className="pointer-events-auto">
        <button
          type="button"
          title="Shot"
          aria-label="Expand shot panel"
          disabled={disabled}
          className={cn(
            "flex size-8 items-center justify-center rounded-md border border-line bg-panel/90 text-muted backdrop-blur-sm",
            "hover:border-accent/40 hover:text-text disabled:opacity-40",
          )}
          onClick={() => setCollapsed(false)}
        >
          <Clapperboard className="size-4" />
        </button>
      </div>
    );
  }

  const endAlt = values.endAltitudeByAspect[aspect];
  const pixels = SHOT_ASPECT_PIXELS[aspect];
  const recorderMime = pickRecorderMimeType();
  const recorderIsMp4 = isMp4Mime(recorderMime);

  return (
    <div className="pointer-events-auto w-56 rounded-md border border-line bg-panel/95 p-2 shadow-lg backdrop-blur-sm">
      <div className="mb-2 flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-text">
          <Clapperboard className="size-3.5 text-accent" />
          Shot
        </div>
        <button
          type="button"
          title="Collapse"
          aria-label="Collapse shot panel"
          className="flex size-6 items-center justify-center rounded text-faint hover:bg-row-hover hover:text-text"
          onClick={() => setCollapsed(true)}
        >
          <ChevronUp className="size-3.5" />
        </button>
      </div>

      <div className="mb-2 space-y-1">
        <label className="block text-xs text-muted">
          Preset
          <select
            className="mt-0.5 h-7 w-full rounded border border-line bg-panel px-1.5 text-xs text-text"
            value={activePresetId}
            disabled={disabled}
            onChange={(e) => setActivePresetId(e.target.value)}
          >
            {presets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
        <div className="flex gap-1">
          <input
            type="text"
            placeholder="Rename / save as…"
            value={saveName}
            disabled={disabled}
            className="h-7 min-w-0 flex-1 rounded border border-line bg-panel px-1.5 text-xs text-text placeholder:text-faint"
            onChange={(e) => setSaveName(e.target.value)}
          />
          <button
            type="button"
            disabled={disabled || !saveName.trim()}
            className="h-7 shrink-0 rounded border border-line px-1.5 text-xs text-muted hover:text-text disabled:opacity-40"
            onClick={() => {
              renameActivePreset(saveName);
              setSaveName("");
            }}
          >
            Rename
          </button>
          <button
            type="button"
            disabled={disabled || !saveName.trim()}
            className="h-7 shrink-0 rounded border border-line px-1.5 text-xs text-muted hover:text-text disabled:opacity-40"
            onClick={() => {
              savePresetAs(saveName);
              setSaveName("");
            }}
          >
            Save
          </button>
        </div>
      </div>

      <label className="mb-2 block text-xs text-muted">
        Aspect
        <select
          className="mt-0.5 h-7 w-full rounded border border-line bg-panel px-1.5 text-xs text-text"
          value={aspect}
          disabled={disabled}
          onChange={(e) => setAspect(e.target.value as ShotAspect)}
        >
          {(Object.keys(SHOT_ASPECT_PIXELS) as ShotAspect[]).map((a) => (
            <option key={a} value={a}>
              {a} ({SHOT_ASPECT_PIXELS[a].width}×{SHOT_ASPECT_PIXELS[a].height})
            </option>
          ))}
        </select>
      </label>

      <div className="mb-2 space-y-2 border-t border-line pt-2">
        <ShotSlider
          label="Shot length"
          value={Math.min(
            SHOT_TOTAL_MAX_MS,
            Math.max(SHOT_TOTAL_MIN_MS, shotTotalDurationMs(values)),
          )}
          min={SHOT_TOTAL_MIN_MS}
          max={SHOT_TOTAL_MAX_MS}
          step={100}
          format={(v) => `${(v / 1000).toFixed(1)}s`}
          onChange={(v) => {
            updateActiveValues(scaleShotTimingToTotal(values, v));
          }}
        />
        <p className="text-xs text-faint">
          Scales flight + holds
          {values.arrivalRipple ? " (ripple fixed)" : ""}
        </p>
        <ShotSlider
          label="Start altitude"
          value={values.startAltitude}
          min={1}
          max={4}
          step={0.05}
          format={(v) => v.toFixed(2)}
          onChange={(v) => updateActiveValues({ startAltitude: v })}
        />
        <ShotSlider
          label={`End altitude (${aspect})`}
          value={endAlt}
          min={0.1}
          max={1.5}
          step={0.05}
          format={(v) => v.toFixed(2)}
          onChange={(v) => setEndAltitudeForAspect(aspect, v)}
        />
        <ShotSlider
          label="Offset longitude"
          value={values.offsetLng}
          min={-90}
          max={90}
          step={1}
          format={(v) => `${v}°`}
          onChange={(v) => updateActiveValues({ offsetLng: v })}
        />
        <ShotSlider
          label="Offset latitude"
          value={values.offsetLat}
          min={-60}
          max={60}
          step={1}
          format={(v) => `${v > 0 ? "+" : ""}${v}°`}
          onChange={(v) => updateActiveValues({ offsetLat: v })}
        />
        <ShotSlider
          label="Flight duration"
          value={values.durationMs}
          min={1000}
          max={10_000}
          step={100}
          format={(v) => `${(v / 1000).toFixed(1)}s`}
          onChange={(v) => updateActiveValues({ durationMs: v })}
        />
        <ShotSlider
          label="Hold before"
          value={values.holdBeforeMs}
          min={0}
          max={3000}
          step={50}
          format={(v) => `${(v / 1000).toFixed(2)}s`}
          onChange={(v) => updateActiveValues({ holdBeforeMs: v })}
        />
        <ShotSlider
          label="Hold after"
          value={values.holdAfterMs}
          min={0}
          max={5000}
          step={50}
          format={(v) => `${(v / 1000).toFixed(2)}s`}
          onChange={(v) => updateActiveValues({ holdAfterMs: v })}
        />
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={values.arrivalRipple}
            disabled={disabled}
            className="size-3.5 accent-accent"
            onChange={(e) => updateActiveValues({ arrivalRipple: e.target.checked })}
          />
          Arrival ripple
        </label>
        <label className="flex items-center gap-2 text-xs text-muted">
          <input
            type="checkbox"
            checked={values.highlightBorder}
            disabled={disabled}
            className="size-3.5 accent-accent"
            onChange={(e) =>
              updateActiveValues({ highlightBorder: e.target.checked })
            }
          />
          Highlight country border
        </label>
      </div>

      <div className="mb-2 space-y-1 border-t border-line pt-2">
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-7 w-full items-center justify-center gap-1.5 rounded border border-line text-xs font-semibold",
            "text-muted hover:border-accent/40 hover:text-text disabled:opacity-40",
          )}
          onClick={() => void onPreview()}
        >
          Preview shot
        </button>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex h-7 w-full items-center justify-center gap-1.5 rounded bg-accent text-xs font-semibold text-accent-contrast",
            "hover:bg-accent-hover disabled:opacity-40",
          )}
          onClick={() => void onRecord()}
        >
          <Video className="size-3.5" />
          Record shot
        </button>
        <button
          type="button"
          disabled={disabled}
          className="flex h-7 w-full items-center justify-center gap-1.5 rounded border border-line text-xs text-muted hover:text-text disabled:opacity-40"
          onClick={() => {
            void (async () => {
              if (!supportsDirectoryPicker()) {
                toast.message("Folder picker unavailable", {
                  description: "Files will download instead. Use Chrome or Edge for a chosen output folder.",
                });
                return;
              }
              const handle = await pickOutputDirectory();
              if (handle) {
                setOutputFolderName(handle.name);
                toast.success(`Output folder: ${handle.name}`);
              }
            })();
          }}
        >
          <FolderOpen className="size-3.5" />
          {outputFolderName ? `Folder: ${outputFolderName}` : "Choose output folder"}
        </button>
        <p className="text-xs text-faint">
          Frame {pixels.width}×{pixels.height} ·{" "}
          {recorderIsMp4 ? "MP4" : "WebM"} · live capture
        </p>
      </div>

      {!codecNoticeDismissed && (
        <div className="rounded border border-line bg-panel-elevated p-1.5 text-xs text-faint">
          <p>
            {recorderIsMp4
              ? "This browser encodes MP4 (H.264). Drop it straight into CapCut / Premiere / IG."
              : "This browser only encodes WebM here. CapCut, Premiere, DaVinci and IG usually take it; otherwise convert externally. No ffmpeg in-app yet."}
          </p>
          <button
            type="button"
            className="mt-1 text-muted hover:text-text"
            onClick={() => dismissCodecNotice()}
          >
            Got it
          </button>
        </div>
      )}
    </div>
  );
}
